import React from 'react';
import { i18n } from 'fishx';
import get from 'lodash/get';
import moment from 'moment';
import {
  renderField,
  BSNL_ATTR,
  preferredMethodList,
  POA_EFF_DATE_FORMAT,
  findAddressName,
  AcqAttrCodes,
  PAYMENT_METHOD,
  PAYMENT_TYPE,
  needCheckAcctModels,
  BankCardType,
} from '@/utils/portalDscm';
import { fixZeroPrecision, defaultCurrencyUnit } from '@/utils/precision';
import { generateDocList } from '@/utils/renderFormItemUtils';

const {
  GENDER,
  TITLE,
  INCOME_GROUP,
  ACCOUNT_TYPE,
  ACCT_SUB_TYPE,
  MARITAL_STATUS,
  ORGANISATION,
  BILL_TYPE,
  CUSTOMER_CLASSIFICATION,
  DO_NOT_CALL_REGISTRY,
} = BSNL_ATTR;

const {
  renderFieldForAttr,
  renderFieldForLinkedAttr,
  renderFieldNormal,
  renderFieldForOccupation,
  renderFieldForCertType,
  renderFieldForAddress,
  renderFieldForBillingCycle,
  renderFieldForBillingMedia,
  renderFieldForSubService,
  renderFieldForPaymentType,
  renderFieldForPaymentMethod,
  renderFieldForBankAcctType,
} = renderField;

export const getFormErrorI18n = path => i18n('FORM_MESSAGE_ERROR_REQUIRED', { label: path });
export const getCustI18n = key => i18n(`CUST_${key}`);
export const getBusinessI18n = key => i18n(`BUSINESS_${key}`);
export const getADDRI18n = key => i18n(`ADDR_${key}`);
export const getServiceI18n = key => i18n(`SERVICE_${key}`);
export const getSearchI18n = key => i18n(`SEARCH_${key}`);
export const getBillingAcctI18n = key => i18n(`ACCT_${key}`);
export const CUST_TYPE_ENU = [
  { label: 'CUST_INDIVIDUAL', value: 'A' },
  { label: 'CUST_CORPORATE', value: 'C' },
];

export const numReg = /^(-)?(\d)*(\.)?(\d)*$/; // 校验纯数字(带小数点)的正则
export const intReg = /^(\d)*$/; // 校验整数
export const emailReg = /^[A-Za-z0-9-_.\u4e00-\u9fa5]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/; // 邮箱正则
export const nameReg = /^([a-zA-z ‘])*$/; // 只允许字母，空格，和 英文字符: ‘ 姓名校验
export const mobileNumberReg = /^0\d{10}$/; // 支持11位，0开头 手机号校验
export const floatReg = /^([0-9]+\.?\d*)$|^(0\.\d*[1-9])$/; // 校验浮点数
export const addressReg = /^([A-Za-z0-9])*$/; // 只允许字母，数字： 地址校验
export const phoneNumberReg = /^\d{10}$/; // 支持10位, 手机号校验

// 存放在开户以及二次业务中用到的表单变量

// 客户信息
// defaultValueMap放置默认值，用于仅展示的场景，比如客户已经存在，展示客户信息
export function customerInfoformItems({
  attrObj,
  handleCustTypeChange,
  handleGenderChange,
  occupationList,
  defaultValueMap = {},
  disabled,
  dateFormat,
  handleCustFirstNameBlur,
  handleCustMiddleNameBlur,
  handleCustLastNameBlur,
  disabledFirstName,
  disabledMiddleName,
  disabledLastName,
}) {
  return [
    {
      label: getCustI18n('CUSTOMER_TYPE'),
      name: 'custType',
      options: { rules: [{ required: true, message: getFormErrorI18n(getCustI18n('CUSTOMER_TYPE')) }], initialValue: 'A' },
      type: 'select',
      data: [
        { label: getCustI18n('INDIVIDUAL'), value: 'A' },
        { label: getCustI18n('CORPORATE'), value: 'C' },
      ],
      renderField: renderFieldNormal,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), onChange: handleCustTypeChange || (() => {}), disabled: true },
    },
    {
      label: getCustI18n('GENDER'),
      name: 'BSNL_GENDER',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getCustI18n('GENDER')) }],
        validate: [],
        initialValue: get(defaultValueMap, 'BSNL_GENDER.attrValue'),
      },
      type: 'select',
      data: get(attrObj, `${GENDER}.valueList`) || [], // 属性查询 BSNL_GENDER，gender变化时联动查询title属性
      renderField: renderFieldForAttr,
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        onChange: handleGenderChange || (() => {}),
        disabled,
        onSelect: () => {},
      },
    },
    {
      label: getCustI18n('TITLE'),
      name: 'BSNL_TITLE',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getCustI18n('TITLE')) }],
        initialValue: get(defaultValueMap, 'BSNL_TITLE.attrValue'),
      },
      type: 'select',
      data: get(attrObj, `${TITLE}.valueList[0].attrValueMark`) ? get(attrObj, `${TITLE}.valueList`) : [], // 属性查询BSNL_TITLE
      renderField: renderFieldForLinkedAttr,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled },
    },
    {
      label: getCustI18n('FIRST_NAME'),
      name: 'firstName',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getCustI18n('FIRST_NAME')) },
          { pattern: nameReg, message: getBusinessI18n('NAME_CHECK') },
        ],
        initialValue: defaultValueMap.firstName,
        getValueFromEvent: e => e.currentTarget.value?.toUpperCase(),
      },
      type: 'input',
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        disabled: disabledFirstName === undefined ? disabled : disabledFirstName,
        onBlur: handleCustFirstNameBlur,
      },
    },
    {
      label: getCustI18n('MIDDLE_NAME'),
      name: 'middleName',
      options: {
        rules: [{ pattern: nameReg, message: getBusinessI18n('NAME_CHECK') }],
        initialValue: defaultValueMap.middleName || defaultValueMap.secondName,
        getValueFromEvent: e => e.currentTarget.value?.toUpperCase(),
      },
      type: 'input',
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        disabled: disabledMiddleName === undefined ? disabled : disabledMiddleName,
        onBlur: handleCustMiddleNameBlur,
      },
    },
    {
      label: getCustI18n('LAST_NAME'),
      name: 'lastName',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getCustI18n('LAST_NAME')) },
          { pattern: nameReg, message: getBusinessI18n('NAME_CHECK') },
        ],
        initialValue: defaultValueMap.thirdName,
        getValueFromEvent: e => e.currentTarget.value?.toUpperCase(),
      },
      type: 'input',
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        disabled: disabledLastName === undefined ? disabled : disabledLastName,
        onBlur: handleCustLastNameBlur,
      },
    },

    {
      label: getCustI18n('DATE_OF_BIRTH'),
      name: 'birthdayDay',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getCustI18n('DATE_OF_BIRTH')) },
          {
            validator: (rule, value, callback) => {
              if (moment().subtract(18, 'years') < moment(value)) {
                callback('Age must be older than 18');
              }
              callback();
            },
          },
        ],
        initialValue: defaultValueMap.birthdayDay,
      }, // TODO 增加年满18岁校验
      type: 'date',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled, format: dateFormat },
    },
    {
      label: getCustI18n('MARITAL_STATUS'),
      name: 'BSNL_MARITAL_STATUS',
      options: { initialValue: get(defaultValueMap, 'BSNL_MARITAL_STATUS.attrValue') },
      type: 'select',
      data: get(attrObj, `${MARITAL_STATUS}.valueList`) || [],
      renderField: renderFieldForAttr,
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getCustI18n('OCCUPATION'),
      name: 'occupation',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getCustI18n('OCCUPATION')) }],
        initialValue: defaultValueMap.occupationId,
      },
      type: 'select',
      data: occupationList || [],
      renderField: renderFieldForOccupation,
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getCustI18n('INCOME_GROUP'),
      name: 'BSNL_INCOME_GROUP',
      options: { initialValue: get(defaultValueMap, 'INCOME_GROUP.attrValue') },
      type: 'select',
      data: get(attrObj, `${INCOME_GROUP}.valueList`) || [],
      renderField: renderFieldForAttr,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled },
    },
    {
      label: getCustI18n('ORIGANISATION'),
      name: 'BSNL_ORGANISATION',
      options: { initialValue: get(defaultValueMap, 'BSNL_ORGANISATION.attrValue') },
      type: 'select',
      data: get(attrObj, `${ORGANISATION}.valueList`) || [],
      renderField: renderFieldForAttr,
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
  ];
}

// 身份证明
export function POIFormItems({
  certTypeList,
  disabled,
  defaultValueMap,
  dateFormat,
  poiEffDateLabel = getBusinessI18n('EFF_DATE'),
  documentNbrValidator = () => {},
  handlePOIDocTypeChange = () => {},
  isPoiExpRequire = false,
}) {
  const { certTypeId, certNbr, poiEffDate, poiExpDate, issueOrg } = defaultValueMap || {};
  // 新建客户时需要添加校验 (当证件类型是Driving License，Passport时, 必填)
  const EXP_DATE_RULE = isPoiExpRequire ? [{ required: true, message: getFormErrorI18n(getBusinessI18n('EXP_DATE')) }] : [];
  // 查看客户详情时, 初始值赋值时不会触发change
  const effDateLabel =
    disabled && (certTypeId === '101' || certTypeId === '102') ? getBusinessI18n('ISSUE_DATE') : poiEffDateLabel;
  return [
    {
      label: getBusinessI18n('DOCUMENT_TYPE'),
      name: 'certType',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBusinessI18n('DOCUMENT_TYPE')) }],
        initialValue: certTypeId,
      },
      type: 'select',
      data: certTypeList || [], // 选项变化时 查询接口 drm/ding/custService/qryCertType
      renderField: renderFieldForCertType,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled, onChange: handlePOIDocTypeChange },
    },
    {
      label: getBusinessI18n('DOCUMENT_NUMBER'),
      name: 'certNbr',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getBusinessI18n('DOCUMENT_NUMBER')) },
          { validator: documentNbrValidator },
        ],
        initialValue: certNbr,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: effDateLabel,
      name: 'poiEffDate',
      options: { rules: [{ required: true, message: getFormErrorI18n(effDateLabel) }], initialValue: poiEffDate },
      type: 'date',
      formItemCompProps: { disabled, format: dateFormat, disabledDate: current => current && current > moment() },
    },
    {
      label: getBusinessI18n('EXP_DATE'),
      name: 'poiExpDate',
      options: { rules: EXP_DATE_RULE, initialValue: poiExpDate },
      type: 'date',
      formItemCompProps: { disabled, format: dateFormat, disabledDate: current => current && current < moment() },
    },
    {
      label: getBusinessI18n('ISSUE_ORG'),
      name: 'poiIssueOrg',
      options: { rules: [{ required: true, message: getFormErrorI18n(getBusinessI18n('ISSUE_ORG')) }], initialValue: issueOrg },
      type: 'input',
      formItemCompProps: { disabled, format: dateFormat },
    },
  ];
}

// 地址证明   Customer Address Info => Proof of Address Details
export function POAFormItems({
  certTypeList,
  disabled,
  defaultValueMap,
  dateFormat,
  poaEffDateLabel = getBusinessI18n('EFF_DATE'),
  isPoaExpRequire,
  handlePOADocTypeChange = () => {},
  documentNbrValidator = (rule, value, callback) => {
    callback();
  },
}) {
  const { certType, certNbr, poaEffDate, issueOrg, poaExpDate } = defaultValueMap || {};
  // 新建客户时需要添加校验 (当证件类型是Driving License，Passport时, 必填)
  const EXP_DATE_RULE = isPoaExpRequire ? [{ required: true, message: getFormErrorI18n(getBusinessI18n('EXP_DATE')) }] : [];
  // 查看客户详情时, 初始值赋值时不会触发change
  const effDateLabel = disabled && (certType === '101' || certType === '102') ? getBusinessI18n('ISSUE_DATE') : poaEffDateLabel;
  return [
    {
      label: getBusinessI18n('DOCUMENT_TYPE'),
      name: 'poaCertType',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBusinessI18n('DOCUMENT_TYPE')) }],
        initialValue: certType,
      },
      type: 'select',
      data: certTypeList,
      renderField: renderFieldForCertType,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled, onChange: handlePOADocTypeChange },
    },
    {
      label: getBusinessI18n('DOCUMENT_NUMBER'),
      name: 'poaCertNbr',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getBusinessI18n('DOCUMENT_NUMBER')) },
          { validator: documentNbrValidator },
        ],
        initialValue: certNbr,
      }, // TODO证件号码 9-26位
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: effDateLabel,
      name: 'poaEffDate',
      options: { rules: [{ required: true, message: getFormErrorI18n(effDateLabel) }], initialValue: poaEffDate },
      type: 'date',
      formItemCompProps: { disabled, format: dateFormat, disabledDate: current => current && current > moment() },
    },
    {
      label: getBusinessI18n('EXP_DATE'),
      name: 'poaExpDate',
      options: { rules: EXP_DATE_RULE, initialValue: poaExpDate },
      type: 'date',
      formItemCompProps: { disabled, format: dateFormat, disabledDate: current => current && current < moment() },
    },
    {
      label: getBusinessI18n('ISSUE_ORG'),
      name: 'poaIssueOrg',
      options: { rules: [{ required: true, message: getFormErrorI18n(getBusinessI18n('ISSUE_ORG')) }], initialValue: issueOrg },
      type: 'input',
      formItemCompProps: { disabled, format: dateFormat },
    },
  ];
}

// 客户地址
export function addressInfoFormItems({
  stateAddrOpts = [],
  districtOpts = [],
  mainLocalityOpts = [],
  handleMainLocalityChange = () => {},
  handleStateChange = () => {},
  handleDistrictChange = () => {},
  handleSubLocalityChange = () => {},
  subLocalityOpts = [],
  isShift = false,
  shiftRange = '',
  disabled,
  disabledFromPinCodeToSubLocality,
  defaultValueMap,
}) {
  const { address1, address2, postCode, addrExtMap } = defaultValueMap || {};
  const {
    state,
    stateName,
    district,
    districtName,
    mainLocalityCode,
    mainLocalityName,
    subLocalityCode,
    subLocalityName,
    landmark,
    landMark,
    latitude,
    longitude,
  } = addrExtMap || {};

  return [
    {
      label: getADDRI18n('FLAT_HOUSE_NO'),
      name: 'houseNo',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getADDRI18n('FLAT_HOUSE_NO')) },
          { pattern: addressReg, message: getBusinessI18n('ADDRESS_CHECK') },
        ],
        initialValue: address2,
        getValueFromEvent: e => e.currentTarget.value?.toUpperCase(),
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getADDRI18n('APRTMENT_BUILDING'),
      name: 'building',
      options: {
        rules: [],
        initialValue: address1,
        getValueFromEvent: e => e.currentTarget.value?.toUpperCase(),
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getADDRI18n('STATE'),
      name: 'state',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('STATE')) }], initialValue: state || stateName }, // 如果state存在，说明是已存在客户，取state为初始值
      type: 'select',
      renderField: renderFieldForAddress,
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        disabled: disabled || disabledFromPinCodeToSubLocality,
        onChange: handleStateChange,
        showSearch: true,
        optionFilterProp: 'children',
        filterOption: (input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0,
      },
      data: stateAddrOpts,
    },
    {
      label: getADDRI18n('CITY_DISTRICT'),
      name: 'district',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('CITY_DISTRICT')) }],
        initialValue: district || districtName,
      },
      type: 'select',
      renderField: renderFieldForAddress,
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        disabled: disabled || disabledFromPinCodeToSubLocality,
        onChange: handleDistrictChange,
        showSearch: true,
        optionFilterProp: 'children',
        filterOption: (input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0,
      },
      data: districtOpts,
    },
    {
      label: getADDRI18n('MAIN_LOCALITY'),
      name: 'mainLocality',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('MAIN_LOCALITY')) }],
        initialValue: mainLocalityCode || mainLocalityName,
      },
      type: 'select',
      renderField: renderFieldForAddress,
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        onChange: handleMainLocalityChange,
        disabled: disabled || disabledFromPinCodeToSubLocality,
        showSearch: true,
        optionFilterProp: 'children',
        filterOption: (input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0,
      },
      data: mainLocalityOpts,
    },
    {
      label: getADDRI18n('SUB_LOCALITY'),
      name: 'subLocality',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('SUB_LOCALITY')) }],
        initialValue: subLocalityCode || subLocalityName,
      },
      type: 'select',
      renderField: renderFieldForAddress,
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        disabled: disabled || disabledFromPinCodeToSubLocality,
        onChange: handleSubLocalityChange,
        showSearch: true,
        optionFilterProp: 'children',
        filterOption: (input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0,
      },
      data: subLocalityOpts,
    },
    {
      label: getADDRI18n('PIN_CODE'),
      name: 'postCode',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('PIN_CODE')) }], initialValue: postCode },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled: true, showSearch: true },
    },
    {
      label: getADDRI18n('LANDMARK'),
      name: 'landmark',
      options: {
        rules: [{ pattern: addressReg, message: getBusinessI18n('ADDRESS_CHECK') }],
        initialValue: landmark || landMark,
        getValueFromEvent: e => e.currentTarget.value?.toUpperCase(),
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getADDRI18n('LATITUDE'), // 纬度
      name: 'latitude',
      options: {
        initialValue: latitude,
        rules: [
          { pattern: numReg, message: `Latitude ${getBusinessI18n('MUST_NUMBER')}` },
          {
            validator: (rule, value, callback) => {
              if (value) {
                if (value > 180 || value < -180) {
                  callback(`${getBusinessI18n('INPUT_VALID')} ${getBusinessI18n('LATITUDE')}`);
                }
                const dotIndex = value.indexOf('.') !== -1 ? value.indexOf('.') : value.length;
                const beginPart = value.substr(0, dotIndex);
                const lastPart = value.substr(dotIndex + 1);

                if (beginPart.length > 3) {
                  callback(
                    `${getBusinessI18n('LENGTH_OF')} ${getBusinessI18n('LATITUDE')}${getBusinessI18n(
                      'LENGTH_SHOULDER_SHORTER_THAN'
                    )} 3`
                  );
                }
                if (lastPart.length > 4) {
                  callback(
                    `${getBusinessI18n('LENGTH_OF')} ${getBusinessI18n('LATITUDE')}${getBusinessI18n(
                      'LENGTH_SHOULDER_LONGER_THAN'
                    )} 4`
                  );
                }
              }
              callback();
            },
          },
        ],
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getADDRI18n('LONGITUDE'), // 经度
      name: 'longitude',
      options: {
        initialValue: longitude,
        rules: [
          { pattern: numReg, message: `Longtitude ${getBusinessI18n('MUST_NUMBER')}` },
          {
            validator: (rule, value, callback) => {
              if (value) {
                if (value > 90 || value < -90) {
                  callback(`${getBusinessI18n('INPUT_VALID')} ${getBusinessI18n('LONGITUDE')}`);
                }
                const dotIndex = value.indexOf('.') !== -1 ? value.indexOf('.') : value.length;
                const beginPart = value.substr(0, dotIndex);
                const lastPart = value.substr(dotIndex + 1);

                if (beginPart.length > 3) {
                  callback(
                    `${getBusinessI18n('LENGTH_OF')} ${getBusinessI18n('LONGITUDE')}${getBusinessI18n(
                      'LENGTH_SHOULDER_SHORTER_THAN'
                    )} 3`
                  );
                }
                if (lastPart.length > 4) {
                  callback(
                    `${getBusinessI18n('LENGTH_OF')} ${getBusinessI18n('LONGITUDE')}${getBusinessI18n(
                      'LENGTH_SHOULDER_LONGER_THAN'
                    )} 4`
                  );
                }
              }
              callback();
            },
          },
        ],
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: i18n('SHIFT_TRANSFER_ADDRESS_RANGE'),
      name: 'shiftRange',
      options: { initialValue: shiftRange },
      type: isShift && disabled ? 'input' : 'hidden',
      formItemCompProps: { disabled: true },
    },
  ];
}

export function getAddressParams(values) {
  const {
    poaCertType,
    poaCertNbr,
    poaExpDate,
    poaEffDate,
    postCode,
    building,
    houseNo,
    state,
    district,
    landmark,
    longitude,
    latitude,
    mainLocality,
    subLocality,
    poaIssueOrg,
    poaDoc,
  } = values;

  const addressInfo = {
    // 地址信息，用来创建客户地址
    postCode,
    address1: building,
    address2: houseNo,
    landmark,
    longitude,
    latitude,
    stdAddrId: `${subLocality}`,
    addrExtMap: {
      state,
      district,
      landmark,
      longitude, // 经纬度有数值限制
      latitude,
      mainLocalityCode: mainLocality,
      subLocalityCode: `${subLocality}`,
      poaDto: {
        certType: poaCertType,
        certNbr: poaCertNbr,
        effDate: poaEffDate ? poaEffDate.format(POA_EFF_DATE_FORMAT) : undefined,
        expDate: poaExpDate ? poaExpDate.format(POA_EFF_DATE_FORMAT) : undefined,
        poaEffDate,
        poaExpDate,
        attachDtoList: generateDocList(poaDoc),
        issueOrg: poaIssueOrg,
      },
    },
  };

  return addressInfo;
}

// 设置地址相关的预览数据
export function getAddressReviewData({
  values,
  stateAddrOpts,
  districtOpts,
  mainLocalityOpts,
  subLocalityOpts,
  poaCertTypeName,
  expDate,
  effDate,
  attachDtoList,
}) {
  const {
    houseNo,
    building,
    postCode,
    state,
    district,
    mainLocality,
    subLocality,
    landmark,
    latitude,
    longitude,
    poaCertType,
    poaCertNbr,
    poaIssueOrg,
    // 按照需求增加两个字段,订单预览界面需要用
    shiftType,
    shiftDate,
  } = values;
  return {
    faltHoustNo: houseNo,
    apartmentBuilding: building,
    pinCode: postCode,
    state: findAddressName(stateAddrOpts, state) || state,
    cityDistrict: findAddressName(districtOpts, district) || district,
    mainLocality: findAddressName(mainLocalityOpts, mainLocality) || mainLocality,
    subLocality: findAddressName(subLocalityOpts, subLocality) || subLocality,
    landmark,
    latitude,
    longitude,
    poaDto: {
      certType: poaCertTypeName || poaCertType,
      certNbr: poaCertNbr,
      effDate,
      expDate,
      attachDtoList,
      poaIssueOrg,
    },
    shiftType,
    shiftDate,
  };
}

// 联系人信息
export function contactPersonInfoFormItems({ disabled, defaultValueMap, suffix, workPhoneRequired, emailRequired }) {
  const { firstName, secondName, thirdName, mobilePhone, emailAddr, homePhone, officePhone, zipcode, mainComm } =
    defaultValueMap || {};

  const temp = [
    {
      label: getCustI18n('CONTACT_FIRST_NAME'),
      name: 'contactFirstName',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getCustI18n('CONTACT_FIRST_NAME')) },
          { pattern: nameReg, message: getBusinessI18n('NAME_CHECK') },
        ],
        initialValue: firstName,
        getValueFromEvent: e => e.currentTarget.value?.toUpperCase(),
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getCustI18n('CONTACT_MIDDLE_NAME'),
      name: 'contactSecondName',
      options: {
        rules: [{ pattern: nameReg, message: getBusinessI18n('NAME_CHECK') }],
        initialValue: secondName,
        getValueFromEvent: e => e.currentTarget.value?.toUpperCase(),
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getCustI18n('CONTACT_LAST_NAME'),
      name: 'contactLastName',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getCustI18n('CONTACT_LAST_NAME')) },
          { pattern: nameReg, message: getBusinessI18n('NAME_CHECK') },
        ],
        initialValue: thirdName,
        getValueFromEvent: e => e.currentTarget.value?.toUpperCase(),
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getCustI18n('PREFERRED_METHOD'),
      name: 'preferredMethod',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getCustI18n('PREFERRED_METHOD')) }],
        initialValue: mainComm,
      },
      type: 'select',
      renderField: renderFieldNormal,
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
      // 优先联系方式写死 --- 方杰
      data: preferredMethodList,
    },
    {
      label: getCustI18n('CONTACT_MOBILE_NUMBER'),
      name: 'mobilePhone',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getCustI18n('CONTACT_MOBILE_NUMBER')) },
          { pattern: intReg, message: getBusinessI18n('INPUT_CORRECT_PHONE_NUMBER') },
          {
            validator: (rule, value, callback) => {
              // 关于联系人号码的校验，参考2692237
              if (value) {
                if (value.length !== 11) {
                  callback(i18n('MOBILE_NUMBER_LENGTH'));
                } else if (!value.startsWith('0')) {
                  callback(i18n('MOBILE_NUMBER_STARTS_WITH_ZERO'));
                } else if (/([0-9])\1{9}/.test(value.substring(1))) {
                  // 如果去除开头的0之后，剩下的数字全都是一样的，则给出提示
                  callback(i18n('MOBILE_NUMBER_SHOULD_NOT_BE_SAME'));
                } else if (!['06', '07', '08', '09'].some(v => value.startsWith(v))) {
                  callback(i18n('MOBILE_NUMBER_START_WITH'));
                }
              }
              callback();
            },
          },
        ],
        initialValue: mobilePhone,
      },
      type: 'input',
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        suffix,
        disabled,
      },
    },
    {
      label: getBusinessI18n('EMAIL'),
      name: 'emailAddr',
      options: {
        initialValue: emailAddr,
        rules: [
          { pattern: emailReg, message: getBusinessI18n('INPUT_CORRECT_EMAIL_ADDRESS') },
          { required: emailRequired, message: getFormErrorI18n(getBusinessI18n('EMAIL')) },
        ],
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getCustI18n('WORK_PHONE'),
      name: 'officePhone',
      options: {
        initialValue: officePhone,
        rules: [
          { pattern: numReg, message: getBusinessI18n('INPUT_CORRECT_PHONE_NUMBER') },
          { required: workPhoneRequired, message: getFormErrorI18n(getCustI18n('WORK_PHONE')) },
        ],
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getCustI18n('HOME_PHONE'),
      name: 'homePhone',
      options: { rules: [{ pattern: numReg, message: getBusinessI18n('INPUT_CORRECT_PHONE_NUMBER') }], initialValue: homePhone },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getCustI18n('POST_CODE'),
      name: 'zipcode',
      options: { initialValue: zipcode },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
  ];
  // if (disabled) { // 在展示态，不用展示OTP输入控件
  //   temp.splice(4, 1);
  // }
  return temp;
}

// 账户信息
export function billAccountInfoFormItems({
  modelName,
  tooltip,
  handleAccountTypeChange,
  attrObj,
  deliveryMethodList,
  billingCycleTypeList,
  disabled,
  hasSelect,
  defaultValueMap,
  suffix,
  emailRequired,
}) {
  const { acctTypeCode, acctSubTypeCode, billingCycleTypeId, billDeliveryTypeDtoList, email, contactNbr } = defaultValueMap || {};
  const temp = [
    {
      label: i18n('ACCOUNT_TYPE'),
      name: ACCOUNT_TYPE,
      options: { rules: [{ required: true, message: getFormErrorI18n(i18n('ACCOUNT_TYPE')) }], initialValue: acctTypeCode },
      type: 'select',
      data: get(attrObj, `${ACCOUNT_TYPE}.valueList`) || [],
      renderField: renderFieldForAttr,
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        onChange: handleAccountTypeChange,
        disabled: disabled || needCheckAcctModels.includes(modelName),
      },
    },
    {
      label: i18n('ACCOUNT_SUB_TYPE'),
      name: ACCT_SUB_TYPE,
      options: {
        rules: [{ required: true, message: getFormErrorI18n(i18n('ACCOUNT_SUB_TYPE')) }],
        initialValue: acctSubTypeCode,
      },
      type: 'select',
      data: get(attrObj, `${ACCT_SUB_TYPE}.valueList`) || [],
      renderField: renderFieldForLinkedAttr,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled: disabled || needCheckAcctModels.includes(modelName) },
    },
    {
      label: i18n('BILLING_FREQUENCY'),
      name: 'billingFreq',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(i18n('BILLING_FREQUENCY')) }],
        initialValue: billingCycleTypeId,
      },
      type: 'select',
      data: billingCycleTypeList || [],
      renderField: renderFieldForBillingCycle,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled },
    },
    {
      label: (
        <span>
          {i18n('BILLING_MEDIA')}
          {tooltip}
        </span>
      ),
      name: 'billingMedia',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(i18n('BILLING_MEDIA')) }],
        initialValue: get(billDeliveryTypeDtoList, '0.deliveryMethod'),
      },
      type: 'select',
      data: deliveryMethodList || [],
      renderField: renderFieldForBillingMedia,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled },
    },
    {
      label: getBusinessI18n('EMAIL'),
      name: 'emailId',
      options: {
        rules: [
          { pattern: emailReg, message: getBusinessI18n('INPUT_CORRECT_EMAIL_ADDRESS') },
          { required: emailRequired, message: getFormErrorI18n(getBusinessI18n('EMAIL')) },
        ],
        initialValue: email,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled },
    },
    {
      label: getBusinessI18n('MOBILE_NUMBER'),
      name: 'mobileNumber',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getBusinessI18n('MOBILE_NUMBER')) },
          {
            validator: (rule, value, callback) => {
              // 关于联系人号码的校验，参考2759235
              if (value) {
                if (value.length !== 11) {
                  callback(i18n('MOBILE_NUMBER_LENGTH'));
                } else if (!value.startsWith('0')) {
                  callback(i18n('MOBILE_NUMBER_STARTS_WITH_ZERO'));
                } else if (/([0-9])\1{9}/.test(value.substring(1))) {
                  // 如果去除开头的0之后，剩下的数字全都是一样的，则给出提示
                  callback(i18n('MOBILE_NUMBER_SHOULD_NOT_BE_SAME'));
                } else if (!['06', '07', '08', '09'].some(v => value.startsWith(v))) {
                  callback(i18n('MOBILE_NUMBER_START_WITH'));
                }
              }
              callback();
            },
          },
        ],
        initialValue: contactNbr,
      },
      type: 'input',
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        suffix,
        disabled,
      },
    },
  ];
  if (disabled) {
    // 在展示态，就不用展示OTP输入控件
    temp.splice(6, 1);
  }
  if (hasSelect) {
    // 非新建客户在展示账户的时候是有下拉选框，这个下拉框写在了页面上，所以这地方要放置空对象占位
    temp.unshift({});
  }
  return temp;
}

export function billAccountInfoForModifyFormItems({
  tooltip,
  attrObj,
  deliveryMethodList,
  billingCycleTypeList,
  disabled,
  defaultValueMap,
  suffix,
  emailRequired,
}) {
  const { acctTypeCode, acctSubTypeCode, billingCycleTypeId, billDeliveryTypeDtoList, email, contactNbr, billType } =
    defaultValueMap || {};
  const temp = [
    {
      label: i18n('ACCOUNT_TYPE'),
      name: ACCOUNT_TYPE,
      options: { rules: [{ required: true, message: getFormErrorI18n(i18n('ACCOUNT_TYPE')) }], initialValue: acctTypeCode },
      type: 'select',
      data: get(attrObj, `${ACCOUNT_TYPE}.valueList`) || [],
      renderField: renderFieldForAttr,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled: true },
    },
    {
      label: i18n('ACCOUNT_SUB_TYPE'),
      name: ACCT_SUB_TYPE,
      options: {
        rules: [{ required: true, message: getFormErrorI18n(i18n('ACCOUNT_SUB_TYPE')) }],
        initialValue: acctSubTypeCode,
      },
      type: 'select',
      data: get(attrObj, `${ACCT_SUB_TYPE}.valueList`) || [],
      renderField: renderFieldForLinkedAttr,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled: true },
    },
    {
      label: i18n('BILLING_FREQUENCY'),
      name: 'billingFreq',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(i18n('BILLING_FREQUENCY')) }],
        initialValue: billingCycleTypeId,
      },
      type: 'select',
      data: billingCycleTypeList || [],
      renderField: renderFieldForBillingCycle,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled },
    },
    {
      label: (
        <span>
          {i18n('BILLING_MEDIA')}
          {tooltip}
        </span>
      ),
      name: 'billingMedia',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(i18n('BILLING_MEDIA')) }],
        initialValue: get(billDeliveryTypeDtoList, '0.deliveryMethod'),
      },
      type: 'select',
      data: deliveryMethodList || [],
      renderField: renderFieldForBillingMedia,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled },
    },
    {
      label: getBusinessI18n('EMAIL'),
      name: 'emailId',
      options: { rules: [{ required: emailRequired, message: getFormErrorI18n(getBusinessI18n('EMAIL')) }], initialValue: email },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled: true },
    },
    {
      label: getBusinessI18n('MOBILE_NUMBER'),
      name: 'mobileNumber',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBusinessI18n('MOBILE_NUMBER')) }],
        initialValue: contactNbr,
      },
      type: 'input',
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        suffix,
        disabled: true,
      },
    },
    {
      label: getBusinessI18n('BILL_TYPE'),
      name: 'billType',
      options: { rules: [{ required: true, message: getFormErrorI18n(getBusinessI18n('BILL_TYPE')) }], initialValue: billType },
      type: 'select',
      data: get(attrObj, `${BILL_TYPE}.valueList`) || [],
      renderField: renderFieldForAttr,
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), suffix },
    },
  ];

  return temp;
}

export function paymentProfileFormItems({
  paymentTypeDtoList,
  paymentMethodDtoList,
  bankAcctTypeDtoList,
  onPaymentTypeChange,
  onPaymentMethodChange,
  defaultValueMap,
  modelName,
  getFieldValue,
} = {}) {
  const {
    paymentType,
    paymentMethod,
    bankAcctNbr,
    bankAcctName,
    bankBranchNbr,
    bankBranchName,
    bankId,
    limit,
    expiration,
    accountType,
    ifsc,
    micr,
    umrn,
  } = defaultValueMap || {};
  return [
    {
      label: getBillingAcctI18n('PAYMENT_TYPE'),
      name: 'paymentType',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBillingAcctI18n('PAYMENT_TYPE')) }],
        initialValue: paymentType,
      },
      type: 'select',
      data: paymentTypeDtoList || [],
      renderField: renderFieldForPaymentType,
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        onChange: typeof onPaymentTypeChange === 'function' ? onPaymentTypeChange : () => {},
      },
    },
    {
      label: getBillingAcctI18n('PAYMENT_METHOD'),
      name: 'paymentMethod',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBillingAcctI18n('PAYMENT_METHOD')) }],
        initialValue: paymentMethodDtoList.find(p => p.paymentMethodId === paymentMethod) ? paymentMethod : undefined,
      },
      type: 'select',
      data: paymentMethodDtoList || [],
      renderField: renderFieldForPaymentMethod,
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        onChange: typeof onPaymentMethodChange === 'function' ? onPaymentMethodChange : () => {},
      },
    },

    {
      label: getBillingAcctI18n('NACH_LIMIT'),
      name: 'limit',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getBillingAcctI18n('NACH_LIMIT')) },
          {
            validator: (rule, value, callback) => {
              if (!floatReg.test(value)) {
                callback('Please input valid limit');
              }
              callback();
            },
          },
        ],
        initialValue: limit,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
      dynamic: getValue => getValue('paymentType') === PAYMENT_TYPE.AUTO && getValue('paymentMethod') === PAYMENT_METHOD.NACH,
    },
    {
      label: getBillingAcctI18n('ECS_LIMIT'),
      name: 'limit',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getBillingAcctI18n('ECS_LIMIT')) },
          {
            validator: (rule, value, callback) => {
              if (!floatReg.test(value)) {
                callback('Please input valid limit');
              }
              callback();
            },
          },
        ],
        initialValue: limit,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
      dynamic: getValue => getValue('paymentType') === PAYMENT_TYPE.AUTO && getValue('paymentMethod') === PAYMENT_METHOD.ECS,
    },
    {
      label: getBillingAcctI18n('ACCOUNT_TYPE'), // 账本类型
      name: 'accountType',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBillingAcctI18n('ACCOUNT_TYPE')) }],
        initialValue: accountType,
      },
      type: 'select',
      data: bankAcctTypeDtoList || [],
      renderField: renderFieldForBankAcctType,
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), showSearch: true },
      dynamic: getValue =>
        // ECS = 103, NACH = 104
        (
          getValue('paymentMethod') === PAYMENT_METHOD.ECS ||
          getValue('paymentMethod') === PAYMENT_METHOD.NACH ||
          getValue('paymentMethod') === PAYMENT_METHOD.CREDIT_CARD
        )
      ,
    },
    {
      label: getBillingAcctI18n('BANK_ACCOUNT_NUMBER'),
      name: 'bankAcctNbr',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getBillingAcctI18n('BANK_ACCOUNT_NUMBER')) },
          {
            validator: (rule, value, callback) => {
              if (value) {
                if (value.length <= 6 || value.length > 20) {
                  callback(getBillingAcctI18n('BANK_ACCT_NBR_VALIDATOR'));
                }
              }
              callback();
            },
          },
        ],
        initialValue: bankAcctNbr,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
      dynamic: getValue => (
        getValue('paymentType') === PAYMENT_TYPE.AUTO &&
          (getValue('paymentMethod') === PAYMENT_METHOD.ECS || getValue('paymentMethod') === PAYMENT_METHOD.NACH)
      ),
    },
    {
      label: getBillingAcctI18n('BANK_ACCOUNT_NAME'),
      name: 'bankAcctName',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBillingAcctI18n('BANK_ACCOUNT_NAME')) }],
        initialValue: bankAcctName,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
      dynamic: getValue =>
        // ECS = 103, NACH = 104
        (
          getValue('paymentType') === PAYMENT_TYPE.AUTO &&
          (getValue('paymentMethod') === PAYMENT_METHOD.ECS || getValue('paymentMethod') === PAYMENT_METHOD.NACH)
        )
      ,
    },
    {
      label: getBillingAcctI18n('BANK_BRANCH_NUMBER'),
      name: 'bankBranchNbr',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBillingAcctI18n('BANK_BRANCH_NUMBER')) }, {}],
        initialValue: bankBranchNbr,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
      dynamic: getValue => getValue('paymentType') === PAYMENT_TYPE.AUTO && getValue('paymentMethod') === PAYMENT_METHOD.ECS,
    },
    {
      label: getBillingAcctI18n('BANK_BRANCH_NAME'),
      name: 'bankBranchName',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBillingAcctI18n('BANK_BRANCH_NAME')) }, {}],
        initialValue: bankBranchName,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
      dynamic: getValue => getValue('paymentType') === PAYMENT_TYPE.AUTO && getValue('paymentMethod') === PAYMENT_METHOD.ECS,
    },
    {
      label: getBillingAcctI18n('BANK_NAME'),
      name: 'bankId',
      options: { rules: [{ required: true, message: getFormErrorI18n(getBillingAcctI18n('BANK_NAME')) }], initialValue: bankId },
      type: 'bankSelect',
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        modelName,
      },
      dynamic: getValue => (
        getValue('paymentType') === PAYMENT_TYPE.AUTO &&
          (getValue('paymentMethod') === PAYMENT_METHOD.ECS || getFieldValue('paymentMethod') === PAYMENT_METHOD.NACH)
      ),
    },
    {
      label: getBillingAcctI18n('CREDIT_CARD_NBR'),
      name: 'bankAcctNbr', // 对应bankAcctNbr字段
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getBillingAcctI18n('CREDIT_CARD_NBR')) },
          {
            validator: (rule, value, callback) => {
              if (value) {
                switch (getFieldValue('accountType')) {
                  case BankCardType.MASTERCard: // MASTER Card
                    if (value.length !== 16) {
                      callback(getBillingAcctI18n('MASTER_CARD_RULE'));
                    }
                    break;
                  case BankCardType.DinersClub: // Diners Club
                    if (value.length !== 14 || !(value.startsWith('30') || value.startsWith('36') || value.startsWith('38'))) {
                      callback(getBillingAcctI18n('DINERS_CLUB_CARD_RULE'));
                    }
                    break;
                  case BankCardType.AmericanExpress: // American Express
                    if (value.length !== 15 || !(value.startsWith('34') || value.startsWith('37'))) {
                      callback(getBillingAcctI18n('AMERICAN_EXPRESS_CARD_RULE'));
                    }
                    break;
                  case BankCardType.Discover: // Discover
                    if (value.length !== 16 || !value.startsWith('6')) {
                      callback(getBillingAcctI18n('DISCOVER_CARD_RULE'));
                    }
                    break;
                  case BankCardType.Visa: // Visa
                    if (value.length !== 16 || !value.startsWith('4')) {
                      callback(getBillingAcctI18n('VISA_CARD_RULE'));
                    }
                    break;
                  default:
                    callback();
                    break;
                }
              }
              callback();
            },
          },
        ],
        initialValue: bankAcctNbr,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
      dynamic: getValue => getValue('paymentType') === PAYMENT_TYPE.MANUAL && getValue('paymentMethod') === PAYMENT_METHOD.CREDIT_CARD,
    },
    {
      label: getBillingAcctI18n('CREDIT_CARD_EXPIRATION'),
      name: 'expiration', // 对应expiration字段
      options: { rules: [], initialValue: expiration ? moment(expiration) : undefined },
      type: 'date',
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        initialValue: expiration,
        disabledDate: current => current && current < moment(),
        picker: 'month',
      },
      dynamic: getValue => getValue('paymentType') === PAYMENT_TYPE.MANUAL && getValue('paymentMethod') === PAYMENT_METHOD.CREDIT_CARD,
    },
    {
      label: getBillingAcctI18n('CREDIT_CARD_HOLDER'),
      name: 'bankAcctName', // 对应bankAcctNname字段
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBillingAcctI18n('CREDIT_CARD_HOLDER')) }],
        initialValue: bankAcctName,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
      dynamic: getValue => getValue('paymentType') === PAYMENT_TYPE.MANUAL && getValue('paymentMethod') === PAYMENT_METHOD.CREDIT_CARD,
    },
    {
      label: 'IFSC Code', // 账本类型
      name: 'ifsc',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getBillingAcctI18n('IFSC')) },
          {
            validator: (rule, value, callback) => {
              if (value && !/^[A-Z]{4}[0]{1}[0-9a-zA-Z]{6}$/.test(value)) {
                // 前四位必须是大写字母，第五位是0，接下来是六位字母和数字
                callback(getBillingAcctI18n('IFSC_MSG'));
              }
              callback();
            },
          },
        ],
        initialValue: ifsc,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), showSearch: true },
      dynamic: getValue =>
        // ECS = 103, NACH = 104
        getValue('paymentType') === PAYMENT_TYPE.AUTO && getValue('paymentMethod') === PAYMENT_METHOD.NACH
      ,
    },
    {
      label: 'MICR Code', // 账本类型
      name: 'micr',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getBillingAcctI18n('MICR')) },
          {
            validator: (rule, value, callback) => {
              if (value && !/^[0-9]{9}$/.test(value)) {
                // 九位数字
                callback(getBillingAcctI18n('MICR_MSG'));
              }
              callback();
            },
          },
        ],
        initialValue: micr,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), showSearch: true },
      dynamic: getValue =>
        // ECS = 103, NACH = 104
        getValue('paymentType') === PAYMENT_TYPE.AUTO && getValue('paymentMethod') === PAYMENT_METHOD.NACH
      ,
    },
    {
      label: 'UMRN Code', // 账本类型
      name: 'umrn',
      options: {
        rules: [
          { required: true, message: getFormErrorI18n(getBillingAcctI18n('UMRN')) },
          {
            validator: (rule, value, callback) => {
              if (value && !/^[0-9a-zA-Z]{20}$/.test(value)) {
                callback(getBillingAcctI18n('UMRN_MSG'));
              }
              callback();
            },
          },
        ],
        initialValue: umrn,
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), showSearch: true },
      dynamic: getValue =>
        // ECS = 103, NACH = 104
        getValue('paymentType') === PAYMENT_TYPE.AUTO && getValue('paymentMethod') === PAYMENT_METHOD.NACH
      ,
    },
  ];
}

// 账户地址信息
export function billAddressInfoFormItems({
  handlePinCodeBlur,
  stateAddrOpts,
  districtOpts,
  mainLocalityOpts,
  subLocalityOpts,
  disabled,
  hasSelect = true, // 默认为true，表示有下拉
  defaultValueMap = {},
  disabledFromPinCodeToSubLocality,
  ...params
}) {
  const addressItems = addressInfoFormItems({
    handlePinCodeBlur,
    stateAddrOpts,
    districtOpts,
    mainLocalityOpts,
    subLocalityOpts,
    disabled,
    defaultValueMap,
    disabledFromPinCodeToSubLocality,
    ...params,
  });
  // 这地方为什么要在头部塞入一个空对象？
  // 因为账户信息的地址在展示时第一个控件可能是一个select下拉(下拉值是所有可选账户)，这个下拉是直接写在页面上的
  // 所以这个空对象就是这个下拉的占位，避免renderFormItems里再进行补全
  // 假如页面不需要展示下拉框，那么就不需要补全
  if (hasSelect) {
    addressItems.unshift({});
  }

  return addressItems;
}

// 服务类型表单
export function serviceFormItems({ serviceType, orgCode, defaultValueMap = {} }) {
  const { serviceTypeName, subServiceTypeDtoList } = serviceType || {};
  const { subServiceType } = defaultValueMap || {};
  const temp = [
    {
      label: getServiceI18n('FR_CODE'),
      name: 'frCode',
      options: { rules: [{ required: true, message: getFormErrorI18n(getServiceI18n('FR_CODE')) }], initialValue: orgCode },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT'), disabled: true },
    },
    {
      label: `${serviceTypeName} ${getServiceI18n('SUB_SERVICE_TYPE')}`,
      name: 'subServiceType',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(`${serviceTypeName} ${getServiceI18n('SUB_SERVICE_TYPE')}`) }],
        initialValue: subServiceType,
      },
      type: 'select',
      renderField: renderFieldForSubService,
      data: subServiceTypeDtoList || [],
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT') },
    },
  ];
  return temp;
}

// 订户信息里的账户信息
export function billAccountInfoForSubsFormItems({ disabled, defaultValueMap }) {
  const { acctTypeName, acctSubTypeName, billingCycleTypeName, billDeliveryTypeDtoList, email, contactNbr, acctNbr } =
    defaultValueMap || {};
  return [
    {
      label: i18n('ACCOUNT_TYPE'),
      name: ACCOUNT_TYPE,
      options: { rules: [{ required: true, message: getFormErrorI18n(i18n('ACCOUNT_TYPE')) }], initialValue: acctTypeName },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: i18n('ACCOUNT_SUB_TYPE'),
      name: ACCT_SUB_TYPE,
      options: {
        rules: [{ required: true, message: getFormErrorI18n(i18n('ACCOUNT_SUB_TYPE')) }],
        initialValue: acctSubTypeName,
      },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: i18n('BILLING_ACCOUNT_NO'),
      name: 'acctNbr',
      options: { rules: [{ required: true, message: getFormErrorI18n(i18n('BILLING_ACCOUNT_NO')) }], initialValue: acctNbr },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: i18n('BILLING_FREQUENCY'),
      name: 'billingFreq',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(i18n('BILLING_FREQUENCY')) }],
        initialValue: billingCycleTypeName,
      },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: i18n('BILLING_MEDIA'),
      name: 'billingMedia',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(i18n('BILLING_MEDIA')) }],
        initialValue: get(billDeliveryTypeDtoList, '0.deliveryMethodName'),
      },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getBusinessI18n('EMAIL'),
      name: 'emailId',
      options: { initialValue: email },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getBusinessI18n('MOBILE_NUMBER'),
      name: 'mobileNumber',
      options: { initialValue: contactNbr },
      type: 'input',
      formItemCompProps: { disabled },
    },
  ];
}

export function serviceBasicInfoForSubsFormItems({ disabled, defaultValueMap }) {
  const { serviceTypeName, prodName, accNbr, stateName } = defaultValueMap || {};
  return [
    {
      label: getSearchI18n('SERVICE_TYPE'),
      name: 'serviceType',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getSearchI18n('SERVICE_TYPE')) }],
        initialValue: serviceTypeName,
      },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getSearchI18n('SUB_SERVICE_TYPE'),
      name: 'subServiceType',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getSearchI18n('SUB_SERVICE_TYPE')) }],
        initialValue: prodName,
      },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getSearchI18n('SERVICE_NUMBER'),
      name: 'accNbr',
      options: { rules: [{ required: true, message: getFormErrorI18n(getSearchI18n('SERVICE_NUMBER')) }], initialValue: accNbr },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getBusinessI18n('STATUS'),
      name: 'status',
      options: { rules: [{ required: true, message: getFormErrorI18n(getBusinessI18n('STATUS')) }], initialValue: stateName },
      type: 'input',
      renderField: renderFieldForBillingCycle,
      formItemCompProps: { disabled },
    },
  ];
}

export function addressInfoForSubsFormItems({ disabled, defaultValueMap }) {
  const {
    houseNo,
    road,
    pinCode,
    stateName,
    districtName,
    mainLocalityName,
    subLocalityName,
    landmark,
    latitude,
    longitude,
    // exchangeCode,
  } = defaultValueMap || {};
  return [
    {
      label: getADDRI18n('FLAT_HOUSE_NO'),
      name: 'houseNo',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('FLAT_HOUSE_NO')) }], initialValue: houseNo },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getADDRI18n('APRTMENT_BUILDING'),
      name: 'building',
      options: { rules: [], initialValue: road },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getADDRI18n('PIN_CODE'),
      name: 'postCode',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('PIN_CODE')) }], initialValue: pinCode },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getADDRI18n('STATE'),
      name: 'state',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('STATE')) }], initialValue: stateName },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getADDRI18n('CITY_DISTRICT'),
      name: 'district',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('CITY_DISTRICT')) }],
        initialValue: districtName,
      },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getADDRI18n('MAIN_LOCALITY'),
      name: 'mainLocality',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('MAIN_LOCALITY')) }],
        initialValue: mainLocalityName,
      },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getADDRI18n('SUB_LOCALITY'),
      name: 'subLocality',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('SUB_LOCALITY')) }],
        initialValue: subLocalityName,
      },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getADDRI18n('LANDMARK'),
      name: 'landmark',
      options: { rules: [], initialValue: landmark },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getADDRI18n('LATITUDE'),
      name: 'latitude',
      options: { initialValue: latitude },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getADDRI18n('LONGITUDE'),
      name: 'longitude',
      options: { initialValue: longitude },
      type: 'input',
      formItemCompProps: { disabled },
    },
  ];
}

export function planInfoForSubsFormItems({
  disabled,
  defaultValueMap,
  offerName,
  // planTimeUnitList,
}) {
  const {
    mrc,
    // cycleQuantity,
    // timeUnit,
    displayMrc,
  } = defaultValueMap || {};
  // const timeUnitName = get(planTimeUnitList.find(item => `${item.vlaue}` === `${timeUnit}`), 'label', '');
  return [
    {
      label: getBusinessI18n('PLAN_NAME'),
      name: 'planName',
      options: { rules: [{ required: true, message: getFormErrorI18n(getBusinessI18n('PLAN_NAME')) }], initialValue: offerName },
      type: 'input',
      formItemCompProps: { disabled },
    },
    {
      label: getBusinessI18n('PRICE'),
      name: 'price',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getBusinessI18n('PRICE')) }],
        initialValue: displayMrc || mrc ? `${defaultCurrencyUnit}${displayMrc || (mrc && fixZeroPrecision(mrc))}` : null,
      },
      type: 'input',
      formItemCompProps: { disabled },
    },
    // {
    //   label: getBusinessI18n('CONTRACT_TERM'),
    //   name: 'cycleQuantity',
    //   options: { initialValue: cycleQuantity ? `${cycleQuantity} ${timeUnitName}` : '' },
    //   type: 'input',
    //   formItemCompProps: { disabled },
    // },
  ];
}
// 创建线索基础信息表单
export function leaselineBaseInfoFormItems() {
  const formItems = [
    {
      label: i18n('CREATE_LEASED_LEAD_NAME'),
      name: 'leadName',
      options: { rules: [{ required: true, message: getFormErrorI18n(i18n('CREATE_LEASED_LEAD_NAME')) }] },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
    },
  ];
  return formItems;
}
// 创建线索的类型表单
export function serviceFormItemsForLeasedLine({ serviceType, orgCode }) {
  const { subServiceTypeDtoList } = serviceType || {};
  const temp = [
    {
      label: getServiceI18n('FR_CODE'),
      name: 'frCode',
      options: { rules: [{ required: true, message: getFormErrorI18n(getServiceI18n('FR_CODE')) }], initialValue: orgCode },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), disabled: true },
    },
    {
      label: getServiceI18n('SUB_SERVICE_TYPE'),
      name: 'subServiceType',
      options: { rules: [{ required: true, message: getFormErrorI18n(getServiceI18n('SUB_SERVICE_TYPE')) }] },
      type: 'select',
      renderField: renderFieldForSubService,
      data: subServiceTypeDtoList || [],
      formItemCompProps: { placeholder: i18n('PLEASE_SELECT') },
    },
  ];
  return temp;
}

// 创建线索的服务类型表单
export function custInfoFormItemForLeasedLine() {
  const temp = [
    {
      label: getSearchI18n('CUSTOMER_NAME'), // Customer Name
      name: 'custName',
      options: {
        rules: [{ required: true, message: getFormErrorI18n(getSearchI18n('CUSTOMER_NAME')) }],
        getValueFromEvent: e => e.currentTarget.value?.toUpperCase(),
      },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
    },
    {
      label: i18n('MULTITENANCYMGR_TENANCY_CONTACT_NAME'), // Contact Name
      name: 'bsnlContactName',
      options: { getValueFromEvent: e => e.currentTarget.value?.toUpperCase() },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
    },
    {
      label: getSearchI18n('MOBILE_NUMBER'), // Mobile Number
      name: 'phone',
      options: { rules: [{ required: true, message: getFormErrorI18n(getSearchI18n('MOBILE_NUMBER')) }] },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
    },
    {
      label: getSearchI18n('Email'), // Email
      name: 'email',
      options: { rules: [{ type: 'email' }] },
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
    },
  ];
  return temp;
}

// 创建线索的地址表单
export function custAddrFormItemsForLeasedLine({
  stateAddrOpts = [],
  districtOpts = [],
  mainLocalityOpts = [],
  handleStateChange = () => {},
  handleDistrictChange = () => {},
  handleSubLocalityChange = () => {},
  handleMainLocalityChange = () => {},
  subLocalityOpts = [],
}) {
  // const {
  //   stateAddrOpts = [],
  //   districtOpts = [],
  //   mainLocalityOpts = [],
  //   handleStateChange,
  //   handleDistrictChange,
  //   handleSubLocalityChange,
  //   handleMainLocalityChange,
  //   subLocalityOpts = [],
  // } = params;
  return [
    {
      label: getADDRI18n('STATE'),
      name: 'state',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('STATE')) }] },
      type: 'select',
      renderField: renderFieldForAddress,
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        onChange: handleStateChange,
        showSearch: true,
        optionFilterProp: 'children',
        filterOption: (input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0,
      },
      data: stateAddrOpts,
    },
    {
      label: getADDRI18n('CITY_DISTRICT'),
      name: 'district',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('CITY_DISTRICT')) }] },
      type: 'select',
      renderField: renderFieldForAddress,
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        onChange: handleDistrictChange,
        showSearch: true,
        optionFilterProp: 'children',
        filterOption: (input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0,
      },
      data: districtOpts,
    },
    {
      label: getADDRI18n('MAIN_LOCALITY'),
      name: 'mainLocality',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('MAIN_LOCALITY')) }] },
      type: 'select',
      renderField: renderFieldForAddress,
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        onChange: handleMainLocalityChange,
        showSearch: true,
        optionFilterProp: 'children',
        filterOption: (input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0,
      },
      data: mainLocalityOpts,
    },
    {
      label: getADDRI18n('SUB_LOCALITY'),
      name: 'subLocality',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('SUB_LOCALITY')) }] },
      type: 'select',
      renderField: renderFieldForAddress,
      formItemCompProps: {
        placeholder: i18n('PLEASE_INPUT'),
        onChange: handleSubLocalityChange,
        showSearch: true,
        optionFilterProp: 'children',
        filterOption: (input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0,
      },
      data: subLocalityOpts,
    },
    {
      label: getADDRI18n('PIN_CODE'),
      name: 'postCode',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('PIN_CODE')) }] },
      type: 'input',
      formItemCompProps: {
        disabled: true,
        placeholder: i18n('PLEASE_INPUT'),
      },
    },
    {
      label: getADDRI18n('ADDRESS_DETAIL'),
      name: 'addressDetail',
      options: { rules: [{ required: true, message: getFormErrorI18n(getADDRI18n('ADDRESS_DETAIL')) }] },
      type: 'textarea',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
    },
  ];
}

// 搜索线索的地址表单
export function searchLeasedLineFormItems({ searchData, dataIsDisabled, onTimeChange }) {
  return [
    {
      label: i18n('LEASE_LINE_LEAD_ID'),
      name: 'leadId',
      options: {},
      type: 'input',
      formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
    },
    {
      label: i18n('TT_ORDER_FORM_CREATE_TIME_FORM'),
      name: 'createStartDate',
      options: {},
      type: 'date',
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        initialValue: searchData.createStartDate,
        disabledDate: dataIsDisabled,
        onChange: value => onTimeChange({ createStartDate: value }),
      },
    },
    {
      label: i18n('TT_ORDER_FORM_CREATE_DATA_TO'),
      name: 'createEndDate',
      options: {},
      type: 'date',
      formItemCompProps: {
        placeholder: i18n('PLEASE_SELECT'),
        initialValue: searchData.createEndDate,
        disabledDate: current => dataIsDisabled(current, true),
        onChange: value => onTimeChange({ createEndDate: value }),
      },
    },
  ];
}

// 设备采集表单
// 设备信息使用value判断。以下为最新value枚举值与必填关系。

// 1） 设备获取方式CPE_ACQUISITION_TYPE,枚举值：
// Owned 自带
// Purchased  通过bsnl的实物销售渠道购买
// PurchaseNextBill 通过crm订购渠道购买
// Rental   租用运营商设备
// FranchiseeRental 租用代理商设备
// PurchaseRecovered 买的回收设备，目前看不需要采集其他东西
// FranchiseePurchase 购买代理商设备，也必须采集BSNL_DEVICE_FRANCHISEE_CODE

// 2) 设备购买回执单号BSNL_DEVICE_RECEIPT_NUMBER：
//     Purchased 方式购买必填

// 3) 设备租用代理商BSNL_DEVICE_FRANCHISEE_CODE：
// FranchiseeRental方式租赁必填
// FranchiseePurchase必填

// 4) 设备租用费计算周期BSNL_DEVICE_RENTAL_TYPE：
// Rental   和 FranchiseeRental 租赁方式必填

// eslint-disable-next-line max-len
export const acquisitionItems = (attrGroup, rentalTypeGroup, acqValue, attrDetail, handleAcqTypeChange, getFormItemName) => [
  {
    label: i18n('OFFER_INSTALLATION_REQUIRED'),
    name: getFormItemName ? getFormItemName(AcqAttrCodes.REQUIRED) : AcqAttrCodes.REQUIRED,
    type: 'select',
    data: [
      { value: 'Y', label: i18n('COMMON_LABEL_YES') },
      { value: 'N', label: i18n('COMMON_LABEL_NO') },
    ],
    renderField: {
      lableField: 'label',
      valueField: 'value',
    },
    options: {
      initialValue: get(attrDetail, AcqAttrCodes.REQUIRED),
      rules: [{ required: true, message: getFormErrorI18n(i18n('OFFER_INSTALLATION_REQUIRED')) }],
    },
    formItemCompProps: {
      placeholder: i18n('PLEASE_SELECT'),
    },
  },
  {
    label: i18n('SELECT_OFFER_ACQUISITION_TYPE'),
    name: getFormItemName ? getFormItemName(AcqAttrCodes.CPE_ACQUISITION_TYPE) : AcqAttrCodes.CPE_ACQUISITION_TYPE,
    type: 'select',
    data: attrGroup,
    options: {
      initialValue: get(attrDetail, AcqAttrCodes.CPE_ACQUISITION_TYPE),
      rules: [{ required: true, message: getFormErrorI18n(i18n('SELECT_NUMBER')) }],
    },
    renderField: {
      lableField: 'valueMark',
      valueField: 'value',
    },
    formItemCompProps: {
      placeholder: i18n('PLEASE_SELECT'),
      onChange: handleAcqTypeChange,
    },
  },
  {
    label: i18n('RELATEED_OFFER_DEVICE_RECEIPT_NO'),
    name: getFormItemName ? getFormItemName(AcqAttrCodes.RECEIPT_NUMBER) : AcqAttrCodes.RECEIPT_NUMBER,
    type: 'input',
    options: {
      initialValue: get(attrDetail, AcqAttrCodes.RECEIPT_NUMBER),
      rules: [{ required: acqValue === 'Purchased', message: getFormErrorI18n(i18n('RELATEED_OFFER_DEVICE_RECEIPT_NO')) }],
    },
    formItemCompProps: {
      disabled: acqValue !== 'Purchased',
      placeholder: i18n('PLEASE_INPUT'),
    },
  },
  {
    label: i18n('RELATEED_OFFER_DEVICE_FRANCHISEE'),
    name: getFormItemName ? getFormItemName(AcqAttrCodes.FRANCHISEE) : AcqAttrCodes.FRANCHISEE,
    type: 'input',
    options: {
      initialValue: get(attrDetail, AcqAttrCodes.FRANCHISEE),
      rules: [
        {
          required: ['FranchiseeRental', 'FranchiseePurchase'].includes(acqValue),
          message: getFormErrorI18n(i18n('RELATEED_OFFER_DEVICE_FRANCHISEE')),
        },
      ],
    },
    formItemCompProps: {
      disabled: !['FranchiseeRental', 'FranchiseePurchase'].includes(acqValue),
      placeholder: i18n('PLEASE_INPUT'),
    },
  },
  {
    label: i18n('RELATEED_OFFER_DEVICE_RENTAL_TYPE'),
    name: getFormItemName ? getFormItemName(AcqAttrCodes.RENTAL_TYPE) : AcqAttrCodes.RENTAL_TYPE,
    type: 'select',
    data: rentalTypeGroup || [],
    renderField: {
      lableField: 'valueMark',
      valueField: 'value',
    },
    options: {
      initialValue: get(attrDetail, AcqAttrCodes.RENTAL_TYPE),
      rules: [
        {
          required: ['Rental', 'FranchiseeRental'].includes(acqValue),
          message: getFormErrorI18n(i18n('RELATEED_OFFER_DEVICE_RENTAL_TYPE')),
        },
      ],
    },
    formItemCompProps: {
      disabled: !['Rental', 'FranchiseeRental'].includes(acqValue),
      placeholder: i18n('PLEASE_INPUT'),
    },
  },
];

export const modifyPwdFormItems = (suffix, defaultValueMap) => [
  {
    label: i18n('MODIFY_VERIFY_TYPE'),
    name: 'verifyType',
    options: { rules: [], initialValue: defaultValueMap.verifyType },
    type: 'select',
    data: [
      { label: 'Mobile Number', value: 'nbr' },
      { label: 'Email', value: 'email' },
    ],
    renderField: { valueField: 'value', lableField: 'label' },
    formItemCompProps: { placeholder: i18n('PLEASE_INPUT') },
  },
  {
    label: i18n('BUSINESS_MOBILE_NUMBER'),
    name: 'mobileNumber',
    options: { rules: [], initialValue: defaultValueMap.account },
    type: 'input',
    formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), suffix },
    dynamic: getFieldValue => {
      if (getFieldValue('verifyType') ? getFieldValue('verifyType') === 'nbr' : defaultValueMap.verifyType === 'nbr') {
        return true;
      }
      return false;
    },
  },
  {
    label: i18n('SEARCH_Email'),
    name: 'email',
    options: { rules: [], initialValue: defaultValueMap.account },
    type: 'input',
    formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), suffix },
    dynamic: getFieldValue => {
      if (getFieldValue('verifyType') ? getFieldValue('verifyType') === 'email' : defaultValueMap.verifyType === 'email') {
        return true;
      }
      return false;
    },
  },
  {
    label: i18n('MODIFY_NEW_PWD'),
    name: 'newPwd',
    options: { rules: [] },
    type: 'input',
    formItemCompProps: { placeholder: i18n('PLEASE_INPUT'), type: 'password' },
    dynamic: getFieldValue => {
      if (getFieldValue('verifyType') || defaultValueMap.verifyType) {
        return true;
      }
      return false;
    },
  },
];

export const customerInfoForModifyFormItems = params => {
  const temp = customerInfoformItems(params);
  const { attrObj, defaultValueMap } = params;
  temp.push({
    label: i18n('MODIFY_VIP_FLAG'),
    name: 'BSNL_CUSTOMER_CLASSIFICATION',
    options: {
      rules: [{ required: true, message: getFormErrorI18n(i18n('MODIFY_VIP_FLAG')) }],
      initialValue: get(defaultValueMap, 'BSNL_CUSTOMER_CLASSIFICATION.attrValue'),
    },
    type: 'select',
    data: get(attrObj, `${CUSTOMER_CLASSIFICATION}.valueList`),
    renderField: renderFieldForAttr,
    formItemCompProps: { placeholder: i18n('PLEASE_SELECT') },
  });
  temp.push({
    label: i18n('MODIFY_DO_NOT_CALL_REGISTRY'),
    name: 'BSNL_DO_NOT_CALL_REGISTRY',
    options: {
      rules: [{ required: true, message: getFormErrorI18n(i18n('MODIFY_DO_NOT_CALL_REGISTRY')) }],
      initialValue: get(defaultValueMap, 'BSNL_DO_NOT_CALL_REGISTRY.attrValue'),
    },
    type: 'select',
    data: get(attrObj, `${DO_NOT_CALL_REGISTRY}.valueList`),
    renderField: renderFieldForAttr,
    formItemCompProps: { placeholder: i18n('PLEASE_SELECT') },
  });

  return temp;
};
